import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Ref } from "@melo/shared";
import { send } from "../lib/actions";
import { useTables } from "../lib/store";
import { keyOf } from "../lib/select";
import { toast } from "../lib/toast";
import { LAYER, useDismiss } from "./primitives";
import { Icon } from "./icons";
import { ItemLink } from "./ItemLink";

const sizeText = (b: number | null) => (b == null ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

export function RefThumb({ refItem, onOpen, cover, onCover }: { refItem: Ref; onOpen: () => void; cover?: boolean; onCover?: () => void }) {
  return (
    <div className={`ref-thumb${cover ? " is-cover" : ""}`}>
      <button type="button" className="ref-open" onClick={onOpen} aria-label={`Open ${refItem.title || "reference"}`}>
        {refItem.type === "image" ? (
          <img src={refItem.url} alt={refItem.title} loading="lazy" draggable={false} />
        ) : (
          <span className="ref-file">
            <Icon name={refItem.type === "link" ? "link" : "file"} size={20} />
            <span>{refItem.title || refItem.url}</span>
          </span>
        )}
      </button>
      {onCover && (
        <button type="button" className="ref-cover-btn" onClick={onCover} title={cover ? "Remove as cover" : "Use as cover"} aria-label={cover ? "Remove as cover" : "Use as cover"} aria-pressed={cover}>
          {cover ? "Cover" : "Set cover"}
        </button>
      )}
    </div>
  );
}

/** Full view of a reference with its title, notes and tags, all editable. */
export function Lightbox({ refItem, onClose }: { refItem: Ref; onClose: () => void }) {
  const t = useTables();
  const live = t.refs.get(refItem.id) ?? refItem;
  const box = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState(live.title);
  const [notes, setNotes] = useState(live.notes);
  const [tags, setTags] = useState(live.tags.join(", "));
  useDismiss(box, onClose, true, LAYER.dialog);
  useEffect(() => {
    setTitle(live.title);
    setNotes(live.notes);
    setTags(live.tags.join(", "));
  }, [live.id]); // eslint-style: only reset when switching references
  const node = live.nodeId ? t.nodes.get(live.nodeId) : undefined;
  const save = () => {
    const nextTags = tags
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (title === live.title && notes === live.notes && nextTags.join(",") === live.tags.join(",")) return;
    void send("ref.update", { refId: live.id, title, notes, tags: nextTags });
  };
  return createPortal(
    <>
      <div className="scrim" style={{ zIndex: 55 }} />
      <div ref={box} className="lightbox" data-layer={LAYER.dialog} role="dialog" aria-modal="true" aria-label={live.title || "Reference"}>
        <div className="lightbox-media">
          {live.type === "image" ? (
            <img src={live.url} alt={live.title} />
          ) : (
            <a className="btn" href={live.url} target="_blank" rel="noopener noreferrer">
              <Icon name={live.type === "link" ? "link" : "file"} /> Open {live.type === "link" ? "link" : "file"}
            </a>
          )}
        </div>
        <div className="lightbox-side">
          <div className="row-gap">
            <span className="eyebrow">{live.type}</span>
            <span className="grow" />
            <button type="button" className="btn ghost icon sm" onClick={onClose} aria-label="Close">
              <Icon name="x" />
            </button>
          </div>
          <div className="field-row">
            <label className="label" htmlFor="ref-title">Title</label>
            <input id="ref-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={save} />
          </div>
          <div className="field-row">
            <label className="label" htmlFor="ref-tags">Tags</label>
            <input id="ref-tags" className="input" value={tags} onChange={(e) => setTags(e.target.value)} onBlur={save} placeholder="bolt_keep, silhouette" />
            <span className="hint">The AI model pipeline pulls references by tag.</span>
          </div>
          <div className="field-row">
            <label className="label" htmlFor="ref-notes">Notes</label>
            <textarea id="ref-notes" className="textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={save} placeholder="What to take from this reference" />
          </div>
          <dl className="ref-meta">
            {live.width && (
              <>
                <dt>Size</dt>
                <dd className="mono">
                  {live.width} × {live.height}
                </dd>
              </>
            )}
            {live.bytes != null && (
              <>
                <dt>File</dt>
                <dd className="mono">{sizeText(live.bytes)}</dd>
              </>
            )}
            <dt>Added by</dt>
            <dd>{t.actors.get(live.createdBy)?.name}</dd>
            {node && (
              <>
                <dt>On</dt>
                <dd>
                  <ItemLink itemKey={keyOf(t, node)}>{node.title}</ItemLink>
                </dd>
              </>
            )}
          </dl>
          <div className="form-actions">
            <button
              type="button"
              className="btn ghost sm"
              onClick={() =>
                navigator.clipboard.writeText(live.url).then(
                  () => toast("File address copied"),
                  () => toast("Could not copy.", { tone: "error" }),
                )
              }
            >
              <Icon name="copy" /> Copy address
            </button>
            <button
              type="button"
              className="btn ghost sm danger"
              onClick={() => {
                void send("ref.archive", { refId: live.id, archived: true }).then(() => {
                  onClose();
                  toast("Reference removed", { action: { label: "Undo", run: () => void send("ref.archive", { refId: live.id, archived: false }) } });
                });
              }}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
